import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type {
  GQLCoverSheet,
  GQLInteractionType,
} from 'src/generated/graph-api';
import { generateCoverSheetQuery } from 'src/views/Coversheet/utils/generateCoverSheetQuery';
import {
  type SanitizedCoverSheetFormData,
  transformCoverSheetToFormData,
} from 'src/views/Coversheet/utils/transformCoverSheetToFormData';
import type { CoverSheetConfigData } from './useCoverSheetConfig';

export const COVERSHEET_INVALIDATE_EVENT = 'coversheet:invalidate';

export function useCoverSheetQuery(
  interactionId: string,
  interactionType: GQLInteractionType,
  coverSheetConfig?: CoverSheetConfigData,
) {
  const client = useApolloClient();
  const [coverSheet, setCoverSheet] = useState<SanitizedCoverSheetFormData>();
  const [loading, setLoading] = useState(true);

  const fetchCoverSheet = useCallback(async () => {
    if (!coverSheetConfig) return;

    const { coverSheetConfig: config } = coverSheetConfig;
    const query = generateCoverSheetQuery(config);
    const { data } = await client.query<{
      coverSheet: Partial<GQLCoverSheet> | null;
    }>({
      query,
      variables: {
        interactionId,
        interactionType,
      },
    });

    if (data?.coverSheet) {
      setCoverSheet(
        transformCoverSheetToFormData(data.coverSheet, config ?? undefined),
      );
    }
    setLoading(false);
  }, [interactionId, interactionType, coverSheetConfig, client]);

  // Setting `loading` in a layout effect hook prevents an extra loading state
  // from being displayed when the config is refetched after the initial render
  useLayoutEffect(() => {
    if (coverSheetConfig) {
      setLoading(true);
    }
  }, [coverSheetConfig]);

  useEffect(() => {
    void fetchCoverSheet();
  }, [fetchCoverSheet]);

  useEffect(() => {
    const handler = () => void fetchCoverSheet();
    window.addEventListener(COVERSHEET_INVALIDATE_EVENT, handler);
    return () =>
      window.removeEventListener(COVERSHEET_INVALIDATE_EVENT, handler);
  }, [fetchCoverSheet]);

  return { data: coverSheet, loading };
}
